'use client';

import { useState, useEffect } from 'react';

export interface UserRole {
  role: string;
  schoolId: string;
  userId: string;
  permissions: string[];
  ready: boolean;
  userData?: any;
}

export function useUserRole() {
  const [userRole, setUserRole] = useState<UserRole>({
    role: '',
    schoolId: '',
    userId: '',
    permissions: [],
    ready: false
  });

  // Add timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!userRole.ready) {
        // console.log('🔍 useUserRole - Timeout reached, setting ready to true');
        setUserRole(prev => ({ ...prev, ready: true }));
      }
    }, 5000); // 5 second timeout

    return () => clearTimeout(timeout);
  }, [userRole.ready]);

  const refreshUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api/v1';
      const response = await fetch(`${apiUrl}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const user = data.user;
        
        // Update localStorage with fresh data
        localStorage.setItem('user', JSON.stringify(user));
        
        // Update state
        const role = user.roles?.[0] || user.role || 'student';
        const schoolId = user.schoolId || user.school?.id;
        const userId = user.id;
        const permissions = user.permissions || [];

        // console.log('🔄 Raw user data from backend:', user);

        // console.log('🔄 useUserRole - Refreshed user data:', {
        //   role,
        //   permissionsCount: permissions.length,
        //   quizPermissions: permissions.filter((p: string) => p.startsWith('quizzes.')),
        //   allPermissions: permissions
        // });

        setUserRole({
          role,
          schoolId,
          userId,
          permissions,
          ready: true,
          userData: user
        });
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  useEffect(() => {
    const initializeUserRole = () => {
      try {
        // Enhanced debugging
        // console.log('🔍 useUserRole - Checking localStorage...');
        const user = localStorage.getItem('user');
        const token = localStorage.getItem('token');
        const school = localStorage.getItem('school');
        /*
        console.log('🔍 useUserRole - localStorage contents:', {
          hasUser: !!user,
          hasToken: !!token,
          hasSchool: !!school,
          userLength: user?.length || 0,
          tokenLength: token?.length || 0
        });
        */

        if (user) {
          const parsedUser = JSON.parse(user);
          // console.log('🔍 useUserRole - Parsed user data:', parsedUser);
          
          const role = parsedUser.roles?.[0] || parsedUser.role || 'student';
          const schoolId = parsedUser.schoolId || parsedUser.school?.id;
          const userId = parsedUser.id;
          const permissions = parsedUser.permissions || [];

          // Debug logging
          /*
          console.log('🔍 useUserRole - Initializing:', {
            role,
            schoolId,
            userId,
            permissionsCount: permissions.length,
            quizPermissions: permissions.filter((p: string) => p.startsWith('quizzes.')),
            hasImport: permissions.includes('quizzes.import'),
            hasExport: permissions.includes('quizzes.export'),
            hasAI: permissions.includes('quizzes.generate_ai_questions'),
            hasFilters: permissions.includes('quizzes.view_filters')
          });
          */

          setUserRole({
            role,
            schoolId,
            userId,
            permissions,
            ready: true,
            userData: parsedUser
          });
        } else {
          // console.log('🔍 useUserRole - No user data found in localStorage');
          // console.log('🔍 useUserRole - Available localStorage keys:', Object.keys(localStorage));
          
          // Try to refresh user data if we have a token but no user data
          const token = localStorage.getItem('token');
          if (token) {
            // console.log('🔍 useUserRole - Token found but no user data, attempting to refresh...');
            refreshUserData();
          } else {
            setUserRole(prev => ({ ...prev, ready: true }));
          }
        }
      } catch (error) {
        console.error('Error initializing user role:', error);
        setUserRole(prev => ({ ...prev, ready: true }));
      }
    };

    initializeUserRole();
  }, []);

  return { ...userRole, refreshUserData };
}
